import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { ISiteCreateModel } from "@/Administration/Models/UI/Sites/ISiteCreateModel";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { SiteCreateState, SiteGmfType } from "../SiteCreate";

interface ISiteInfoSectionProps {
    infoLabel: string;
    clinicalName: string;
    acronym: string;
    clinicalCode: string;
    selectAsMainEnrollmentForClinicalCode: boolean;
    model: ISiteCreateModel;
    unapprovedNoteDays: number;
    unapprovedNoteHours: number;
    unapprovedNoteMinutes: number;
    ceLoginRAMQ: string;
    aipLogin: string;
    mspLocationCode: string;
    mspFacilityNumber: string;
    payeeNumber: string;
    selectedParentSite: SitesModel;
    parentSites: SitesModel[];
    addresses: Address[];
    isGmf: boolean;
    isGmfr: boolean;
    isGmfu: boolean;
    isUnapprovedNoteExpiryHoursValid: () => boolean;
    isUnapprovedNoteExpiryDaysValid: () => boolean;
    isUnapprovedNoteExpiryMinutesValid: () => boolean;
    isPayeeNumberValid: () => boolean;
    isMSPFacilityNumberValid: () => boolean;
    mutateState: (mutation: (state: SiteCreateState) => void, then?: () => void) => void;
}

function SiteInfoSection(props: ISiteInfoSectionProps): JSX.Element {
    const {
        infoLabel,
        clinicalName,
        acronym,
        clinicalCode,
        selectAsMainEnrollmentForClinicalCode,
        model,
        unapprovedNoteDays,
        unapprovedNoteHours,
        unapprovedNoteMinutes,
        ceLoginRAMQ,
        addresses,
        aipLogin,
        mspLocationCode,
        mspFacilityNumber,
        payeeNumber,
        parentSites,
        selectedParentSite,
        isGmf,
        isGmfr,
        isGmfu,
        isUnapprovedNoteExpiryHoursValid,
        isUnapprovedNoteExpiryDaysValid,
        isUnapprovedNoteExpiryMinutesValid,
        isPayeeNumberValid,
        isMSPFacilityNumberValid,
        mutateState
    } = props;
    const minutesInDay = 1440;
    const minutesInHour = 60;
    const textboxControlStyle: React.CSSProperties = { float: "left", width: "50%" };
    const textBoxLabelStyle: React.CSSProperties = { float: "left", paddingLeft: "5px" };
    const filteredMSPServiceLocationCodes: { code: string; description: string; obsolete?: boolean; }[] = Object.values(AdministrationHelper.mspServiceLocationCodes).filter(p => !p.obsolete || p.code === mspLocationCode);

    const defaultAddress = Address.getDefaultAddress(addresses);

    function hasAtLeastOneChildInQuebec(): boolean {
        return cacheStore.SiteCache
            .filter(s => s.Model.ParentSiteId == model.get("Model").Id)
            .any(s => s.isSiteInProvince(CanadaState.Quebec));
    }

    function onMspLocationCodeChange(e: React.FormEvent): void {
        const selectedMspLocationCode = (e.target as HTMLSelectElement)?.value;

        mutateState(s => s.mspLocationCode = selectedMspLocationCode);
    }

    function onSiteParentChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        // This is there for now, will modify whn ill be able to add other parent site. Value will always be defaulted to the first parent site in the parent site list
        const parentSiteId = e.target.value;
        mutateState(s => {
            parentSites.map(p => {
                if (p.Model.Id === parentSiteId) {
                    s.selectedParentSite = p;
                }
            });
        });
    }

    return (
        <div>
            <div className="view-section-header">
                <h3 className="view-section-title">{cacheStore.GetString(infoLabel)}</h3>
            </div>
            <div className="view-section-body">
                <div className="row" style={{ marginBottom: "15px" }}>
                    <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                        <label className="form-label">{cacheStore.GetString("NameLabel")}</label>
                    </div>
                    <div className="col-lg-5 col-md-9 col-sm-12 ">
                        <TextboxControl
                            valid={!IsNullOrWhiteSpace(clinicalName)}
                            debounceMs={200}
                            className="form-control"
                            onChange={name => mutateState(s => s.clinicalName = name)}
                            value={clinicalName}
                        />
                    </div>
                </div>
                <div className="row" style={{ marginBottom: "15px" }}>
                    <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                        <label className="form-label">{cacheStore.GetString("AcronymLabel")}</label>
                    </div>
                    <div className="col-lg-5 col-md-9 col-sm-12 ">
                        <TextboxControl
                            valid={!IsNullOrWhiteSpace(acronym)}
                            debounceMs={200}
                            maxLength={50}
                            className="form-control"
                            onChange={acronym => mutateState(s => s.acronym = acronym)}
                            value={acronym}
                        />
                    </div>
                </div>
                <div className="row" style={{ marginBottom: "15px" }}>
                    <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                        <label className="form-label">{model.get("IsOrganization") ? cacheStore.GetString("OrganizationCodeLabel") : cacheStore.GetString("EstablishmentCodeLabel")}</label>
                    </div>
                    <div className="col-lg-5 col-md-9 col-sm-12 ">
                        <TextboxControl
                            valid={!IsNullOrWhiteSpace(clinicalCode)}
                            debounceMs={200}
                            className="form-control"
                            onChange={code => mutateState(s => s.clinicalCode = code)}
                            value={clinicalCode}
                        />
                    </div>
                </div>
                {Address.hasAddressInProvince(addresses, CanadaState.Quebec) && selectedParentSite && (
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3" />
                        <div className="col-lg-5 col-md-9">
                            <CheckboxControl
                                label={cacheStore.GetString("SelectAsMainEnrollmentSiteForClinicalCodeLabel")}
                                checked={selectAsMainEnrollmentForClinicalCode}
                                onChange={checked => mutateState(s => s.selectAsMainEnrollmentForClinicalCode = checked)}
                            />
                        </div>
                    </div>
                )}
                {model.get("IsOrganization") && (
                    <div className="row" style={{ marginBottom: "15px" }}>

                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("UnapproveNoteExpiryTimeLabel")}</label>
                        </div>

                        <div className="col-lg-5 col-md-5 col-sm-12 vcenter">
                            <div className="row" style={{ marginBottom: "15px" }}>
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <TextboxControl
                                        style={textboxControlStyle}
                                        valid={isUnapprovedNoteExpiryDaysValid()}
                                        debounceMs={200}
                                        maxLength={3}
                                        type="text"
                                        IsNumeric={true}
                                        onChange={totalDays => {
                                            const totalDaysZeroIfNoData = Number(totalDays || "0");
                                            mutateState(s => {
                                                s.unapprovedNoteExpiryInMinutes = (totalDaysZeroIfNoData * minutesInDay) + (unapprovedNoteHours * minutesInHour) + unapprovedNoteMinutes;
                                                s.unapprovedNoteDays = totalDaysZeroIfNoData;
                                            });
                                        }}
                                        value={unapprovedNoteDays.toString()}
                                    />
                                    <label className="form-label" style={textBoxLabelStyle}>{cacheStore.GetString("DaysLabel")}</label>
                                </div>

                                <div className="col-lg-3 col-md-3 col-sm-12 vcenter">
                                    <TextboxControl
                                        style={textboxControlStyle}
                                        valid={isUnapprovedNoteExpiryHoursValid()}
                                        debounceMs={200}
                                        maxLength={2}
                                        type="text"
                                        IsNumeric={true}
                                        onChange={totalHours => {
                                            const totalHoursZeroIfNoData = Number(totalHours || "0");
                                            mutateState(s => {
                                                s.unapprovedNoteExpiryInMinutes = (unapprovedNoteDays * minutesInDay) + (totalHoursZeroIfNoData * minutesInHour) + unapprovedNoteMinutes;
                                                s.unapprovedNoteHours = totalHoursZeroIfNoData;
                                            });
                                        }}
                                        value={unapprovedNoteHours.toString()}
                                    />
                                    <label className="form-label" style={textBoxLabelStyle}>{cacheStore.GetString("HoursLabel")}</label>
                                </div>

                                <div className="col-lg-3 col-md-3 col-sm-12 vcenter">
                                    <TextboxControl
                                        style={textboxControlStyle}
                                        valid={isUnapprovedNoteExpiryMinutesValid()}
                                        debounceMs={200}
                                        maxLength={2}
                                        type="text"
                                        IsNumeric={true}
                                        onChange={totalMinutes => {
                                            const totalMinutesZeroIfNoData = Number(totalMinutes || "0");
                                            mutateState(s => {
                                                s.unapprovedNoteExpiryInMinutes = (unapprovedNoteDays * minutesInDay) + (unapprovedNoteHours * minutesInHour) + totalMinutesZeroIfNoData;
                                                s.unapprovedNoteMinutes = totalMinutesZeroIfNoData;
                                            });
                                        }}
                                        value={unapprovedNoteMinutes.toString()}
                                    />
                                    <label className="form-label" style={textBoxLabelStyle}>{cacheStore.GetString("MinutesLabel")}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {!model.get("IsOrganization") && (
                    <div>
                        {Address.isAddressInProvince(defaultAddress, CanadaState.Quebec) && (
                            <div id="ramqSiteCodes">
                                <div className="row" style={{ marginBottom: "15px" }}>
                                    <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                                        <label className="form-label">{cacheStore.GetString("CodeCELabel")}</label>
                                    </div>
                                    <div className="col-lg-5 col-md-9 col-sm-12 ">
                                        <TextboxControl
                                            debounceMs={200}
                                            className="form-control"
                                            onChange={code => mutateState(s => s.ceLoginRAMQ = code)}
                                            value={ceLoginRAMQ}
                                        />
                                    </div>
                                </div>
                                <div className="row" style={{ marginBottom: "15px" }}>
                                    <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                                        <label className="form-label">{cacheStore.GetString("AipCodeLabel")}</label>
                                    </div>
                                    <div className="col-lg-5 col-md-9 col-sm-12 ">
                                        <TextboxControl
                                            debounceMs={200}
                                            className="form-control"
                                            onChange={aip => mutateState(s => s.aipLogin = aip)}
                                            value={aipLogin}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {Address.isAddressInProvince(defaultAddress, CanadaState.BritishColumbia) && (
                            <div className="row" style={{ marginBottom: "15px" }}>
                                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("MspLocationCodeLabel")}</label>
                                </div>
                                <div className="col-lg-5 col-md-9 col-sm-12 ">
                                    <select onChange={e => onMspLocationCodeChange(e)} className="form-control myle-control" value={mspLocationCode}>
                                        <option value="" />
                                        {filteredMSPServiceLocationCodes.map(
                                            code => <option key={code.code} value={code.code}>{`${code.code} - ${code.description}`}</option>
                                        )}
                                    </select>
                                </div>
                            </div>
                        )}
                        {cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                            <div className="row" style={{ marginBottom: "15px" }}>
                                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("MspFacilityNumberLabel")}</label>
                                </div>
                                <div className="col-lg-5 col-md-9 col-sm-12 ">
                                    <TextboxControl
                                        debounceMs={200}
                                        className="form-control"
                                        max={5}
                                        valid={isMSPFacilityNumberValid()}
                                        onChange={mspFacilityNumber => mutateState(state => state.mspFacilityNumber = mspFacilityNumber)}
                                        value={mspFacilityNumber}
                                    />
                                </div>
                            </div>
                        )}
                        {cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                            <div className="row" style={{ marginBottom: "15px" }}>
                                <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right ">
                                    <label className="form-label">{cacheStore.GetString("ClinicPayeeNumberLabel")}</label>
                                </div>
                                <div className="col-lg-5 col-md-9 col-sm-12 ">
                                    <TextboxControl
                                        debounceMs={200}
                                        className="form-control"
                                        max={5}
                                        valid={isPayeeNumberValid()}
                                        onChange={payeeNumber => mutateState(state => state.payeeNumber = payeeNumber)}
                                        value={payeeNumber}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="row" style={{ marginBottom: "15px" }}>
                            <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("OrganizationLabel")}</label>
                            </div>
                            <div className="col-lg-5 col-md-9 col-sm-12 ">
                                <select onChange={e => onSiteParentChange(e)} className="form-control myle-control" value={selectedParentSite.Model.Id}>
                                    {parentSites.map((parentSite: SitesModel) => (
                                        <option key={parentSite.Model.Id} value={parentSite.Model.Id}> {limitTextLength(parentSite.Model.ClinicalName)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}
                {model.get("IsOrganization") && (
                    hasAtLeastOneChildInQuebec() && (
                        <div className="row" style={{ marginBottom: "15px" }}>
                            <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right" />
                            <div className="col-lg-5 col-md-9 col-sm-12">
                                <InlineOptionsListControl
                                    id="_gmfTypeRadioButtons"
                                    items={[
                                        new InlineOption(SiteGmfType.Gmf, cacheStore.GetString("IsGMFLabel")),
                                        new InlineOption(SiteGmfType.Gmfr, cacheStore.GetString("SuperFamilyMedicineGroupAbbrevLabel")),
                                        new InlineOption(SiteGmfType.Gmfu, cacheStore.GetString("UniversityFamilyMedicineGroupAbbrevLabel"))
                                    ]}
                                    value={isGmf ? SiteGmfType.Gmf :
                                        isGmfr ? SiteGmfType.Gmfr :
                                            isGmfu ? SiteGmfType.Gmfu :
                                                null
                                    }
                                    onChange={(option: InlineOption<SiteGmfType>) => {
                                        mutateState((_state: SiteCreateState) => {
                                            _state.isGmf = option?.Key === SiteGmfType.Gmf;
                                            _state.isGmfr = option?.Key === SiteGmfType.Gmfr;
                                            _state.isGmfu = option?.Key === SiteGmfType.Gmfu;
                                        });
                                    }}
                                    allowDeselect={true}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

export default SiteInfoSection;
